/**
 * FlotaTab -- pestana "Flota" de Desktop Admin: cada PC de cada negocio (FLOTA-PANEL-VISTA-T1).
 *
 * AUDIENCIA: solo el equipo Nexo (Desktop Admin ya es audiencia nexo), escritorio y celular.
 * Poco texto: una linea de resumen y la lista por negocio; cada negocio abre sus PCs y cada PC abre sus banderas.
 * Version en verde si es la ultima publicada; en amarillo con "va N atras" si no. Sin banderas: el PC aun no esta en
 * la 1277 (desde ahi las manda). No pinta huella ni token.
 *
 * @module presentation/views/desktop/FlotaTab
 */
import { VersionApiClient } from '../../../infrastructure/api/VersionApiClient.js';
import { FlotaApiClient } from '../../../infrastructure/api/FlotaApiClient.js';
import { flotaPorNegocio, resumenFlota } from '../../../application/desktop/flotaPcs.js';

const C = { texto: 'var(--text-strong)', suave: 'var(--text-secondary)', linea: 'var(--border-default)', ok: 'var(--accent-green-text)', ojo: 'var(--accent-yellow)', enlace: 'var(--accent-blue)' };
// Deuda 4: era una copia del escape; ahora es el compartido.
import { esc } from '../../shared/texto.js';
const HISTORIAL = 200;

function haceCuanto(iso) {
  const d = new Date(iso);
  if (!iso || isNaN(d.getTime()) || d.getFullYear() < 2000) return 'nunca visto';
  const min = Math.floor((Date.now() - d.getTime()) / 60000);
  if (min < 60) return `visto hace ${Math.max(min, 0)} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `visto hace ${h} h`;
  return `visto hace ${Math.floor(h / 24)} d`;
}

const siNo = (v) => (v === true ? 'si' : v === false ? 'no' : 'sin dato');

function estadoVersion(p) {
  if (!p.version) return `<span data-flota-version style="color:${C.suave}">sin version</span>`;
  if (p.atras === 0) return `<span data-flota-version data-al-dia style="color:${C.ok};white-space:nowrap">${esc(p.version)} · la ultima</span>`;
  if (p.masDe) return `<span data-flota-version data-atrasado style="color:${C.ojo};white-space:nowrap">${esc(p.version)} · va mas de ${p.atras} atras</span>`;
  if (p.atras > 0) return `<span data-flota-version data-atrasado style="color:${C.ojo};white-space:nowrap">${esc(p.version)} · va ${p.atras} atras</span>`;
  return `<span data-flota-version style="color:${C.suave};white-space:nowrap">${esc(p.version)} · no se sabe cuantas va atras</span>`;
}

function pcHtml(p) {
  const b = p.banderas;
  const detalle = b
    ? `<dl style="margin:6px 0 0;display:grid;grid-template-columns:auto 1fr;gap:2px 10px">
        <dt style="color:${C.suave}">Pantalla</dt><dd style="margin:0">${esc(b.interfaz || 'sin dato')}</dd>
        <dt style="color:${C.suave}">Temporizador</dt><dd style="margin:0">${siNo(b.timer)}</dd>
        <dt style="color:${C.suave}">Financiero</dt><dd style="margin:0">${siNo(b.financiero)}</dd>
        <dt style="color:${C.suave}">Clave puesta</dt><dd style="margin:0">${siNo(b.tokenPresente)}</dd>
      </dl>`
    : `<div style="margin-top:6px;color:${C.suave}">Este PC aun no manda su estado: empieza cuando este en la version 1277.</div>`;
  return `<details data-flota-pc style="border-top:1px solid ${C.linea};padding:6px 0">
    <summary style="cursor:pointer;display:flex;flex-wrap:wrap;gap:2px 12px;align-items:baseline">
      <code style="flex:1 1 140px;min-width:0;overflow-wrap:anywhere;font-size:14px">${esc(p.pcName)}</code>
      ${estadoVersion(p)}
      <span style="color:${C.suave};white-space:nowrap">${haceCuanto(p.vistoAt)}</span>
    </summary>${detalle}</details>`;
}

/** Lo pintado, puro sobre los grupos (para el guardia). */
export function flotaHtml(grupos, { banderasPublicadas = true } = {}) {
  const r = resumenFlota(grupos);
  if (!r.pcs) return `<p class="empty-state">No hay PCs registrados en los negocios activos.</p>`;
  const negocios = grupos.map((g) => `<details data-flota-negocio="${esc(g.businessId)}" style="border:1px solid ${C.linea};border-radius:8px;padding:8px 12px;margin-top:8px">
      <summary style="cursor:pointer;display:flex;flex-wrap:wrap;gap:2px 12px;align-items:baseline">
        <strong style="flex:1 1 160px;min-width:0">${esc(g.negocio)}</strong>
        <span style="white-space:nowrap;color:${g.atrasados ? C.ojo : C.suave}">${g.pcs.length} PC${g.pcs.length === 1 ? '' : 's'}${g.atrasados ? `, ${g.atrasados} atrasado${g.atrasados === 1 ? '' : 's'}` : ''}</span>
      </summary>${g.pcs.map(pcHtml).join('')}</details>`).join('');
  // El summary en flex pierde el triangulo (pasado en Pensum, 504): se pone a mano para que se note que abre.
  return `<style>
      [data-flota] summary { list-style: none; }
      [data-flota] summary::-webkit-details-marker { display: none; }
      [data-flota] summary::before { content: '\\25B8'; color: ${C.suave}; flex: 0 0 1em; }
      [data-flota] details[open] > summary::before { content: '\\25BE'; }
      [data-flota] summary:focus-visible { outline: 2px solid ${C.enlace}; outline-offset: 2px; }
    </style><div data-flota style="font-size:14px;color:${C.texto}">
    <div data-flota-resumen>${r.pcs} PCs · <span style="color:${C.ok}">${r.alDia} en la ultima</span> · <span style="color:${C.ojo}">${r.atrasados} atrasados</span>${r.sinSaber ? ` · ${r.sinSaber} sin saber` : ''}</div>
    ${banderasPublicadas ? '' : `<div data-flota-sin-banderas style="color:${C.suave};margin-top:4px">El estado de cada PC aparece cuando la API lo publique.</div>`}
    ${negocios}</div>`;
}

/** Monta la pestana. Recibe lo que Desktop Admin ya cargo (negocios activos y sus maquinas). */
export async function renderFlota(el, businesses, activeMachines) {
  el.innerHTML = '<div class="spinner"></div>';
  const [hist, ban] = await Promise.all([VersionApiClient.getHistory('nexo_desktop', HISTORIAL).catch(() => null), FlotaApiClient.banderas()]);
  const h = hist?.data?.data ?? hist?.data ?? [];
  const publicadas = (Array.isArray(h) ? h : []).map((v) => v.version ?? v.Version).filter(Boolean);
  const grupos = flotaPorNegocio({
    maquinas: activeMachines, negocios: businesses, banderas: ban.ok ? ban.items : [],
    publicadas, historialCompleto: publicadas.length < HISTORIAL
  });
  el.innerHTML = flotaHtml(grupos, { banderasPublicadas: ban.ok });
}
